import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  DEFAULT_ALT_SKILL_LEVEL,
  getEffectStatementsBySubjectAndGrade,
  getGradesSet,
  getSkillTypes,
  getStatementsBySkill,
  populatePlaceholderText,
  returnGradeFromEnrolledGrade
} from '@/presentlevels/api_v1.js'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { levelGroupLabelFor } from '@/presentlevels/no-ai/level_group_label'
import { SKILL_STATEMENT_EVENT_NAMES } from '@/presentlevels/no-ai/event_names'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import type { RadioRowOption } from '@/components/RadioRow.vue'
import type {
  DefaultStatement,
  DefaultStatementsByLevel,
  Skill,
  StatementResource,
  StatementStandard,
  StudentStatement
} from '@/presentlevels/no-ai/skill_statement_types'

export type SkillStatementProps = {
  subject: string
  skill: Skill
  skillType: symbol
  studentName?: string
  studentGrade?: string
  studentStatements: StudentStatement[]
  presentLevelUuid?: string
  isMultiSelect: boolean
  isEffectsTemplate: boolean
}

export type CardState = 'notIncluded' | 'loading' | 'newEdit' | 'saved' | 'savedEdit'

export type SelectedStatement = {
  text: string
  resources: Record<string, StatementResource>
  standardsBySetCode: Record<string, StatementStandard[]>
}

type StatementTextAndResources = {
  text: string
  resourceDocs?: StatementResource[]
}

const NO_FUNCTIONAL_GRADE = 'NoFunctionGrade'
const EFFECT_STATEMENT = 'Effect'
const MASTERY = 'Mastery'
const GRADE_SET_SUBJECTS = ['Math', 'Reading', 'Writing']
const EDITING_STATES: CardState[] = ['newEdit', 'savedEdit']
const COMBINED_HIGH_SCHOOL_GRADES: Record<string, string[]> = {
  '9-10th': ['9th', '10th'],
  '11-12th': ['11th', '12th']
}
const LOADING_STATEMENTS_TEXT = 'Loading skill statements...'
const NO_DEFAULT_STATEMENTS_TEXT =
  'Sorry but we have no default statements for this skill, but you can still write your own statement here!'
const NOTHING_SAVED_TEXT = 'You have not saved a statement for this skill!'
const CHOOSE_A_STATEMENT_TEXT = 'Choose a statement to include and edit.'
const STATEMENT_SAVED_FEEDBACK = 'Statement saved.'
const STATEMENTS_LOAD_ERROR = "Couldn't load statements, please try again"
// The editor shows these in place of a statement, and every save posts what the editor shows.
const PLACEHOLDER_TEXTS = [LOADING_STATEMENTS_TEXT, NO_DEFAULT_STATEMENTS_TEXT, CHOOSE_A_STATEMENT_TEXT]
const RESOURCE_SOURCE = 'present-levels'

const byLevelOrder = (a: DefaultStatement, b: DefaultStatement) => parseInt(a.level_order ?? '') - parseInt(b.level_order ?? '')

const todayAsDateString = () => {
  const today = new Date()
  return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`
}

const resourcesByUuid = (resourceDocs: StatementResource[] = []): Record<string, StatementResource> => {
  const usableResources = resourceDocs.filter((resource) => resource.uuid && !resource.error)
  return Object.fromEntries(usableResources.map((resource) => [resource.uuid, { ...resource, $_source: RESOURCE_SOURCE }]))
}

const levelLabelOf = (statement: StudentStatement) => statement.selected_statement.level_label

export function useSkillStatement(props: SkillStatementProps) {
  const wizardStore = usePresentLevelsWizardStore()
  const { visibleStandardSets, profileId } = storeToRefs(useContentProfileStore())
  const { licenseSubType } = storeToRefs(useUserStore())

  const cardState = ref<CardState>('notIncluded')
  const selectedGrade = ref(props.studentGrade ?? '')
  const defaultStatements = ref<DefaultStatementsByLevel | null>(null)
  const savedStatements = ref<StudentStatement[]>([...props.studentStatements])
  const savedStatement = ref<StudentStatement | null>(savedStatements.value.find((statement) => statement.is_selected) ?? null)
  const editedStatement = ref('')
  const isViewingSaved = ref(false)
  const multiSelectedOptions = ref<string[]>([])
  const wikiTextFeedback = ref('')
  const statementsLoadError = ref('')

  const isImpact = computed(() => props.skillType === getSkillTypes().IMPACT)
  const usesGradeSet = computed(() => !isImpact.value && GRADE_SET_SUBJECTS.includes(props.subject))
  const isEditing = computed(() => EDITING_STATES.includes(cardState.value))
  const contentInfo = computed(() => ({ cu: props.skill.skill_uuid, ct: props.skill.skill_name }))

  const skillTypeName = computed(() => {
    if (props.skillType === getSkillTypes().SKILL) return 'Skill'
    if (isImpact.value) return 'Impact'
    return ''
  })

  const populateDefaultStatement = (statementText: string) =>
    populatePlaceholderText(statementText, {
      studentName: props.studentName,
      studentGrade: props.studentGrade,
      dateStr: todayAsDateString()
    })

  function levelForCardState(): string {
    if (isEditing.value) return selectedGrade.value
    if (cardState.value === 'saved') return savedStatement.value ? levelLabelOf(savedStatement.value) : NO_FUNCTIONAL_GRADE
    return NO_FUNCTIONAL_GRADE
  }

  function combinedHighSchoolGradeFor(grade: string, defaults: DefaultStatementsByLevel): string | undefined {
    return Object.keys(COMBINED_HIGH_SCHOOL_GRADES).find(
      (combinedGrade) => defaults[combinedGrade] && COMBINED_HIGH_SCHOOL_GRADES[combinedGrade].includes(grade)
    )
  }

  // A student's enrolled grade may sit outside the grades a skill has statements for, so it is
  // pulled to the nearest grade that has one.
  function gradeWithinDefaultStatements(grade: string, defaults: DefaultStatementsByLevel): string {
    const grades = getGradesSet()
    const statementGradeIndexes = Object.keys(defaults)
      .filter((level) => level !== MASTERY)
      .map((level) => grades.indexOf(level))
    const highestIndex = Math.max(0, ...statementGradeIndexes)
    const lowestIndex = Math.min(grades.length, ...statementGradeIndexes.filter((index) => index >= 0))
    const gradeIndex = grades.indexOf(grade)

    if (gradeIndex > highestIndex && grade !== MASTERY) return grades[highestIndex]
    if (gradeIndex < lowestIndex) return grades[lowestIndex] ?? grade
    if (defaults[grade] === undefined) return combinedHighSchoolGradeFor(grade, defaults) ?? grade
    return grade
  }

  const functionalGrade = computed({
    get(): string {
      if (!usesGradeSet.value) return levelForCardState()
      if (defaultStatements.value === null) return selectedGrade.value
      return gradeWithinDefaultStatements(selectedGrade.value, defaultStatements.value)
    },
    set(grade: string) {
      selectedGrade.value = grade
      editedStatement.value = ''
      isViewingSaved.value = false
    }
  })

  const defaultStatementForLevel = computed(() => defaultStatements.value?.[functionalGrade.value])

  const firstUnlockedStatement = (defaults: DefaultStatementsByLevel) =>
    Object.values(defaults).sort(byLevelOrder).find((statement) => !statement.locked)

  const defaultStatement = computed((): DefaultStatement => {
    const statement = defaultStatementForLevel.value
    const unlockedStatement = statement?.locked && defaultStatements.value ? firstUnlockedStatement(defaultStatements.value) : statement
    return unlockedStatement ?? { level_label: '', statement_text: '', resource_docs: [] }
  })

  const defaultText = computed(() => populateDefaultStatement(defaultStatement.value.statement_text))

  function editedOrDefaultStatement(defaultForLevel: () => StatementTextAndResources | undefined): StatementTextAndResources {
    if (editedStatement.value) {
      return { text: editedStatement.value, resourceDocs: savedStatement.value?.selected_statement.resource_docs }
    }
    if (defaultStatements.value === null) return { text: LOADING_STATEMENTS_TEXT }
    if (Object.keys(defaultStatements.value).length === 0) return { text: NO_DEFAULT_STATEMENTS_TEXT }
    return defaultForLevel() ?? { text: '' }
  }

  const statementFrom = (statement: StudentStatement): StatementTextAndResources => ({
    text: statement.statement_text,
    resourceDocs: statement.selected_statement.resource_docs
  })

  function savedEditStatement(): StatementTextAndResources {
    const savedForLevel = savedStatements.value.find((statement) => levelLabelOf(statement) === functionalGrade.value)
    if (!editedStatement.value && savedStatement.value && isViewingSaved.value) return statementFrom(savedStatement.value)
    if (!editedStatement.value && savedForLevel) return statementFrom(savedForLevel)
    return editedOrDefaultStatement(() => {
      const statement = defaultStatementForLevel.value
      if (!statement) return undefined
      return { text: populateDefaultStatement(statement.statement_text), resourceDocs: statement.resource_docs }
    })
  }

  function newEditStatement(): StatementTextAndResources {
    return editedOrDefaultStatement(() => {
      if (!defaultStatementForLevel.value) return { text: CHOOSE_A_STATEMENT_TEXT }
      return { text: populateDefaultStatement(defaultStatement.value.statement_text), resourceDocs: defaultStatement.value.resource_docs }
    })
  }

  function statementForCardState(): StatementTextAndResources {
    if (cardState.value === 'saved') return savedStatement.value ? statementFrom(savedStatement.value) : { text: NOTHING_SAVED_TEXT }
    if (cardState.value === 'savedEdit') return savedEditStatement()
    if (cardState.value === 'newEdit') return newEditStatement()
    return { text: '' }
  }

  function standardsBySetCode(standards: StatementStandard[] = []): Record<string, StatementStandard[]> {
    const isVisible = (standard: StatementStandard) =>
      Boolean(standard.standard_long_ref?.trim()) && (visibleStandardSets.value as string[]).includes(standard.standard_set_code)
    return standards.filter(isVisible).reduce<Record<string, StatementStandard[]>>((grouped, standard) => {
      grouped[standard.standard_set_code] = [...(grouped[standard.standard_set_code] ?? []), standard]
      return grouped
    }, {})
  }

  const selectedStatement = computed((): SelectedStatement => {
    const { text, resourceDocs } = statementForCardState()
    return {
      text,
      resources: resourcesByUuid(resourceDocs),
      standardsBySetCode: standardsBySetCode(defaultStatementForLevel.value?.standard_docs)
    }
  })

  const levelOptions = computed((): RadioRowOption[] => {
    const defaults = defaultStatements.value
    if (!defaults) return []
    const levels = usesGradeSet.value
      ? getGradesSet().filter((grade) => defaults[grade])
      : Object.values(defaults).sort(byLevelOrder).map((statement) => statement.level_label)
    return levels.map((label) => ({
      label,
      disabled: false,
      locked: Object.values(defaults).find((statement) => statement.level_label === label)?.locked
    }))
  })

  const hasLevelChoices = computed(() => Object.keys(defaultStatements.value ?? {}).length >= 2)
  const usesLevelingSet = computed(() => levelOptions.value.some(({ label }) => label === DEFAULT_ALT_SKILL_LEVEL))
  const levelLabelPosition = computed(() => (usesGradeSet.value || usesLevelingSet.value ? 'top' : 'right'))

  const levelColumns = computed(() => {
    if (usesGradeSet.value) return 14
    if (usesLevelingSet.value) return 6
    return 2
  })

  const preselectedLevels = computed(() => (props.isMultiSelect ? multiSelectedOptions.value : [functionalGrade.value]))

  const levelGroupLabel = computed(() =>
    levelGroupLabelFor(props.skill.skill_name, { multiSelect: props.isMultiSelect, noun: isImpact.value ? 'Area' : 'Level' })
  )

  const multiSelectedStatements = computed(() =>
    savedStatements.value.filter((statement) => multiSelectedOptions.value.includes(levelLabelOf(statement)))
  )

  const analytics = getAnalyticsTracker().getChildContext({
    plw_statement_uuid: computed(() => props.skill.skill_uuid),
    plw_statement_name: computed(() => props.skill.skill_name),
    plw_statement_level: computed(() => (props.isMultiSelect ? null : functionalGrade.value)),
    plw_statement_type: skillTypeName,
    plw_statement_levels: multiSelectedOptions
  })

  const savedStatementForLevel = (level: string) => savedStatements.value.find((statement) => levelLabelOf(statement) === level)

  function rememberSavedStatement(statement: StudentStatement, level: string) {
    editedStatement.value = ''
    savedStatement.value = statement
    const savedIndex = savedStatements.value.findIndex((saved) => levelLabelOf(saved) === level)
    if (savedIndex >= 0) {
      savedStatements.value.splice(savedIndex, 1, statement)
      return
    }
    savedStatements.value.push(statement)
  }

  async function saveStatement(statementUuid: string, statementText: string, level: string) {
    if (PLACEHOLDER_TEXTS.includes(statementText)) return
    const statement = await wizardStore.saveStudentStatement({
      presentLevelUuid: props.presentLevelUuid ?? '',
      statementUuid,
      subject: props.subject,
      skillUuid: props.skill.skill_uuid,
      statementText,
      defaultStatement: defaultStatements.value?.[level],
      isMultiSelect: props.isMultiSelect
    })
    if (statement) rememberSavedStatement(statement, level)
  }

  function keyedByLevel(statements: DefaultStatement[]): DefaultStatementsByLevel {
    return Object.fromEntries(statements.map((statement) => [statement.level_label, statement]))
  }

  async function fetchEffectStatements() {
    const data = await getEffectStatementsBySubjectAndGrade(props.subject, returnGradeFromEnrolledGrade(props.studentGrade ?? ''))
    const statements: DefaultStatement[] = data.statements
    if (statements.length > 1) {
      defaultStatements.value = keyedByLevel(statements)
      return
    }
    defaultStatements.value = statements.length ? { [EFFECT_STATEMENT]: statements[0] } : {}
    if (statements.length) multiSelectedOptions.value = [EFFECT_STATEMENT]
  }

  async function fetchSkillStatements() {
    const data = await getStatementsBySkill(props.skill.skill_uuid, profileId.value, licenseSubType.value, props.subject)
    defaultStatements.value = keyedByLevel(data.statements)
  }

  async function fetchStatements() {
    try {
      await (props.isEffectsTemplate ? fetchEffectStatements() : fetchSkillStatements())
    } catch (error) {
      console.error(error)
    }
  }

  const fetchStatementsIfMissing = async () => {
    if (defaultStatements.value === null) await fetchStatements()
  }

  const haveDefaultStatementsLoaded = () => defaultStatements.value !== null

  // Cleared before each attempt, so a repeated failure re-renders the alert and is announced again.
  function clearStatementsLoadError() {
    statementsLoadError.value = ''
  }

  function updateStatementsLoadError() {
    statementsLoadError.value = haveDefaultStatementsLoaded() ? '' : STATEMENTS_LOAD_ERROR
  }

  function startingLevel(): string {
    const targetLevel = levelOptions.value.find(({ label }) => label === DEFAULT_ALT_SKILL_LEVEL)
    return (targetLevel ?? levelOptions.value[0])?.label ?? selectedGrade.value
  }

  async function startGradeSetStatement() {
    cardState.value = 'newEdit'
    await saveStatement('', selectedStatement.value.text, functionalGrade.value)
    cardState.value = 'savedEdit'
  }

  function startLevelStatement() {
    if (props.isEffectsTemplate) {
      cardState.value = 'savedEdit'
      return
    }
    cardState.value = 'newEdit'
    selectedGrade.value = startingLevel()
  }

  async function viewStatements() {
    clearStatementsLoadError()
    analytics.sendEvent(SKILL_STATEMENT_EVENT_NAMES.CLICKED_ADD, {}, contentInfo.value)
    if (wizardStore.blockIfDevelopingIsNotAllowed()) return
    if (cardState.value === 'loading' || cardState.value === 'newEdit') return

    cardState.value = 'loading'
    if (defaultStatements.value === null) {
      await fetchStatements()
    } else if (props.isEffectsTemplate && defaultStatements.value[EFFECT_STATEMENT]) {
      multiSelectedOptions.value = [EFFECT_STATEMENT]
    }

    updateStatementsLoadError()
    if (!haveDefaultStatementsLoaded()) {
      cardState.value = 'notIncluded'
      return
    }

    if (usesGradeSet.value) {
      await startGradeSetStatement()
      return
    }
    startLevelStatement()
  }

  async function editSaved() {
    clearStatementsLoadError()
    analytics.sendEvent(SKILL_STATEMENT_EVENT_NAMES.CLICKED_EDIT)
    if (wizardStore.blockIfDevelopingIsNotAllowed()) return
    await fetchStatementsIfMissing()
    updateStatementsLoadError()
    if (haveDefaultStatementsLoaded()) cardState.value = 'savedEdit'
  }

  const clearWikiTextFeedback = () => {
    wikiTextFeedback.value = ''
  }

  async function stopEditing(text: string) {
    isViewingSaved.value = true
    if (text) editedStatement.value = text
    if (cardState.value !== 'savedEdit') return
    await saveStatement(savedStatement.value?.uuid ?? '', selectedStatement.value.text, functionalGrade.value)
    wikiTextFeedback.value = STATEMENT_SAVED_FEEDBACK
  }

  async function stopEditingMultiSelect(text: string, statementUuid: string, level: string) {
    isViewingSaved.value = true
    if (!isEditing.value) return
    cardState.value = 'savedEdit'
    await saveStatement(statementUuid, text, level)
    wikiTextFeedback.value = STATEMENT_SAVED_FEEDBACK
  }

  function closeEdit() {
    if (cardState.value === 'newEdit') {
      cardState.value = 'notIncluded'
    } else if (cardState.value === 'savedEdit') {
      const hasNoSelectedLevels = props.isMultiSelect && multiSelectedOptions.value.length === 0
      cardState.value = hasNoSelectedLevels ? 'notIncluded' : 'saved'
      if (!hasNoSelectedLevels) clearWikiTextFeedback()
    }
    analytics.sendEvent(SKILL_STATEMENT_EVENT_NAMES.CLICKED_CLOSE)
  }

  async function deleteAllStudentStatements(): Promise<boolean> {
    analytics.sendEvent(
      SKILL_STATEMENT_EVENT_NAMES.CLICKED_DELETE,
      { skill_uuid: props.skill.skill_uuid, skill_name: props.skill.skill_name },
      contentInfo.value
    )
    const isDeleted = await wizardStore.deleteAllStudentStatementsForSkill(props.presentLevelUuid ?? '', props.skill.skill_uuid)
    if (!isDeleted) return false
    savedStatement.value = null
    savedStatements.value = []
    return true
  }

  // Clearing the levels after the delete leaves no saved statement for the level watcher to deselect.
  async function removeSaved() {
    clearStatementsLoadError()
    if (!(await deleteAllStudentStatements())) return
    multiSelectedOptions.value = []
    cardState.value = 'notIncluded'
  }

  function selectLevels(levels: string[]) {
    if (props.isMultiSelect) {
      multiSelectedOptions.value = levels
      return
    }
    functionalGrade.value = levels[0]
  }

  function saveSingleLevelChange(level: string) {
    if (cardState.value === 'savedEdit') {
      analytics.sendEvent(SKILL_STATEMENT_EVENT_NAMES.CLICKED_LEVEL, {}, contentInfo.value)
      void saveStatement(savedStatementForLevel(level)?.uuid ?? '', selectedStatement.value.text, functionalGrade.value)
    } else if (cardState.value === 'newEdit') {
      void saveStatement('', selectedStatement.value.text, functionalGrade.value).then(() => {
        cardState.value = 'savedEdit'
      })
    }
  }

  function saveSelectedLevel(level: string, levels: string[], previousLevels: string[]) {
    const saved = savedStatementForLevel(level)
    const defaultForLevel = defaultStatements.value?.[level]
    if (!saved && defaultForLevel) {
      analytics.sendEvent(
        SKILL_STATEMENT_EVENT_NAMES.CLICKED_LEVEL,
        {
          multiselect: props.isMultiSelect,
          plw_statement_level: levels.find((option) => !previousLevels.includes(option)),
          plw_statement_levels: levels
        },
        contentInfo.value
      )
      void saveStatement('', populateDefaultStatement(defaultForLevel.statement_text), level)
      return
    }
    if (saved) void saveStatement(saved.uuid ?? '', saved.statement_text, level)
  }

  function deselectLevel(level: string) {
    const statementUuid = savedStatementForLevel(level)?.uuid
    if (statementUuid) void wizardStore.deselectStudentStatement(props.presentLevelUuid ?? '', statementUuid)
  }

  async function saveMultiSelectedLevels(levels: string[], previousLevels: string[]) {
    await fetchStatementsIfMissing()
    levels.forEach((level) => saveSelectedLevel(level, levels, previousLevels))
    previousLevels.filter((level) => !levels.includes(level)).forEach(deselectLevel)
    if (cardState.value === 'newEdit') cardState.value = 'savedEdit'
  }

  watch(functionalGrade, (level) => {
    if (!props.isMultiSelect) saveSingleLevelChange(level)
  })

  watch(multiSelectedOptions, saveMultiSelectedLevels)

  // The selected levels are set after their watcher exists so that, on load, each selected
  // statement is saved again against its current default statement, as the results page always has.
  function initialiseFromStudentStatements() {
    if (savedStatements.value.length === 0) return
    if (props.isMultiSelect) {
      multiSelectedOptions.value = savedStatements.value.filter((statement) => statement.is_selected).map(levelLabelOf)
      cardState.value = 'saved'
      return
    }
    if (!savedStatement.value) return
    selectedGrade.value = levelLabelOf(savedStatement.value)
    cardState.value = 'saved'
  }

  initialiseFromStudentStatements()

  return {
    cardState,
    isEditing,
    hasLevelChoices,
    levelOptions,
    levelColumns,
    levelLabelPosition,
    levelGroupLabel,
    preselectedLevels,
    functionalGrade,
    multiSelectedStatements,
    selectedStatement,
    defaultText,
    wikiTextFeedback,
    statementsLoadError,
    populateDefaultStatement,
    viewStatements,
    editSaved,
    closeEdit,
    removeSaved,
    stopEditing,
    stopEditingMultiSelect,
    clearWikiTextFeedback,
    selectLevels
  }
}
